import { Box, Drawer, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useIntercomHorizontalPadding } from 'pages/intercom/useIntercomHorizontalPadding';
import { useTranslator } from 'translation/useTranslator';

export const SIDE_PANEL_DEFAULT_WIDTH = 560;
const SIDE_PANEL_MIN_WIDTH = 400;

const MaximizedContext = createContext(false);

export const useSidePanelMaximized = () => useContext(MaximizedContext);

type SidePanelProps = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  defaultWidth?: number;
  dataTestId: string;
};

const SidePanel = ({
  open,
  onClose,
  title,
  subtitle,
  footer,
  children,
  defaultWidth = SIDE_PANEL_DEFAULT_WIDTH,
  dataTestId
}: SidePanelProps) => {
  const translator = useTranslator();
  const [width, setWidth] = useState(defaultWidth);
  const [maximized, setMaximized] = useState(false);
  const [resizing, setResizing] = useState(false);
  const [headerAnchor, setHeaderAnchor] = useState<HTMLDivElement | null>(null);
  useIntercomHorizontalPadding(open, headerAnchor);

  useEffect(() => {
    if (!resizing) {
      return;
    }

    const onMouseMove = (event: MouseEvent) => {
      const dragged = window.innerWidth - event.clientX;
      setWidth(Math.min(Math.max(dragged, SIDE_PANEL_MIN_WIDTH), window.innerWidth));
    };
    const stopResizing = () => setResizing(false);

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', stopResizing);
    document.body.style.userSelect = 'none';

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', stopResizing);
      document.body.style.removeProperty('user-select');
    };
  }, [resizing]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: maximized ? { width: '100vw' } : { width, maxWidth: '94vw' }
        }
      }}
    >
      {!maximized && (
        <Box
          data-testid={`${dataTestId}-resize-handle`}
          onMouseDown={() => setResizing(true)}
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            width: 6,
            cursor: 'col-resize',
            zIndex: 1,
            '&:hover': { bgcolor: 'primary.light' },
            bgcolor: resizing ? 'primary.main' : 'transparent'
          }}
        />
      )}

      <Stack
        ref={setHeaderAnchor}
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        sx={{ p: 3, pb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Stack gap={0.5}>
          <Typography variant="h6">{title}</Typography>
          {subtitle}
        </Stack>
        <Stack direction="row" gap={0.5}>
          <Tooltip title={maximized ? translator.minimize() : translator.maximize()}>
            <IconButton
              data-testid={`${dataTestId}-maximize`}
              onClick={() => setMaximized((previous) => !previous)}
              size="small"
            >
              {maximized ? (
                <CloseFullscreenIcon fontSize="small" />
              ) : (
                <OpenInFullIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <IconButton data-testid={`${dataTestId}-close`} onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      <MaximizedContext.Provider value={maximized}>
        <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>{children}</Box>
      </MaximizedContext.Provider>

      {footer && (
        <Stack
          direction="row"
          justifyContent="flex-end"
          gap={1.5}
          sx={{ p: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}
        >
          {footer}
        </Stack>
      )}
    </Drawer>
  );
};

export default SidePanel;
